import { useEffect, useState, useRef } from '@wordpress/element';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import AddressSearch from './addresssearch';
import Vendor from './vendor';

mapboxgl.accessToken = 'pk.eyJ1IjoiY29kZW1pcSIsImEiOiJjbHYzZ212N3EwMG92MmtrODhkdjY0N2hwIn0.eD7HeYhnlpSNfXJ-dqxlVw';

export default function VendorSelect({ classification = 'CUZ', onVendorSelect = (vendor) => { }, preselectedVendorId = null, postalCode = null }) {
  const [vendors, setVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [lng, setLng] = useState(10.679319);
  const [lat, setLat] = useState(49.984492);
  const [zoom, setZoom] = useState(6);
  const [markers, setMarkers] = useState({});
  const [searchCenterMarker, setSearchCenterMarker] = useState(null);

  const [filteredVendors, setFilteredVendors] = useState([]);
  const [filter, setFilter] = useState({
    lat: 0,
    lng: 0,
  });

  // useEffect(() => {
  //   if (classification) {
  //     loadVendors();
  //   }
  // }, [])

  useEffect(() => {
    if (classification) {
      loadVendors();
    }
  }, [classification])

  useEffect(() => {
    filterVendors(vendors, true);
  }, [filter, vendors])

  useEffect(() => {
    if (vendors.length == 0) return;
    initMap();
  }, [vendors]);

  // Vendor vorauswählen wenn preselectedVendorId gesetzt ist
  useEffect(() => {
    if (!preselectedVendorId || vendors.length === 0) return;
    const vendor = vendors.find(v => String(v.id) === String(preselectedVendorId));
    if (vendor) {
      onVendorSelect(vendor);
      // Karte auf den Händler zentrieren sobald die Map geladen ist
      const flyToVendor = () => {
        if (map.current && map.current.loaded()) {
          map.current.flyTo({ center: [vendor.lng, vendor.lat], zoom: 12 });
          if (markers[vendor.id]) {
            markers[vendor.id].togglePopup();
          }
        }
      };
      if (map.current && map.current.loaded()) {
        flyToVendor();
      } else if (map.current) {
        map.current.once('load', flyToVendor);
      }
    }
  }, [vendors, markers, preselectedVendorId]);

  // Event Delegation für Händler-Auswahl in Popups
  useEffect(() => {
    const handleVendorSelect = (e) => {
      if (e.target.classList.contains('select-vendor')) {
        e.preventDefault();
        let vendorId = e.target.getAttribute('data-vendor-id');
        if (vendorId) {
          vendorId = parseInt(vendorId, 10);

          const vendor = vendors.find(v => v.id === vendorId);
          if (vendor) {
            onVendorSelect(vendor);
          }
        }
      }
    };

    const container = mapContainer.current;
    if (container) {
      container.addEventListener('click', handleVendorSelect);
      return () => {
        container.removeEventListener('click', handleVendorSelect);
      };
    }
  }, [onVendorSelect, vendors]);


  async function loadVendors() {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    const response = await fetch(`/wp-json/cqvs/v1/vendors?classification=${encodeURIComponent(classification)}`);

    if (!response.ok) {
      // oups! something went wrong
      setIsLoading(false);
      return;
    }

    let newVendors = await response.json();

    setVendors(newVendors);

    setIsLoading(false);
  }

  const initMap = () => {
    if (map.current) return; // initialize map only once
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [lng, lat],
      zoom: zoom,
      minZoom: 6,
      maxZoom: 18
    });
    map.current.on('move', () => {
      setLng(map.current.getCenter().lng.toFixed(4));
      setLat(map.current.getCenter().lat.toFixed(4));
      setZoom(map.current.getZoom().toFixed(2));
    });
    map.current.on('load', () => {
      // Add navigation control (the +/- zoom buttons)
      //map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      const newMarkers = {};
      // create markers from vendors
      vendors.forEach((vendor) => {
        // console.log("add marker for ", vendor);
        newMarkers[vendor.id] = new mapboxgl.Marker({ color: '#009BB4', scale: 0.5 })
          .setLngLat([vendor.lng, vendor.lat])
          .setPopup(new mapboxgl.Popup({ offset: 25 }) // add popups
            .setHTML(getVendorMarkerHTML(vendor))
          )
          .addTo(map.current);
      });
      setMarkers(newMarkers);
    });
  }

  const getVendorMarkerHTML = (vendor) => {
    return "<p class='vendor-title'>" + vendor.title + "</p>\
      <div class='vendor-address'>\
        <p>" + vendor.street + "\
          <br />" + vendor.zipcode + " " + vendor.city + "\
        </p>\
        <div class='cta-wrap'>\
            <a href='#' rel='noopener noreferrer' class='select-vendor' data-vendor-id='" + vendor.id + "'>\
              Händler auswählen\
            </a>\
          </div>\
      </div>\
      ";
  }


  const filterVendors = (newVendors, fitBounds = false) => {
    if (newVendors.length == 0) return;
    if (filter.lat == 0 && filter.lng == 0) {
      setFilteredVendors([]);
      return;
    }

    let max_distance = 1.0;
    let vendorsWithDistance = [];
    for (let i = 0; i < newVendors.length; i++) {
      const distance = Math.sqrt(
        Math.pow(newVendors[i].lat - filter.lat, 2) +
        Math.pow(newVendors[i].lng - filter.lng, 2)
      );
      const distanceKm = distance * 111.32;
      let distanceDescription = '';
      if (distanceKm < 10) {
        distanceDescription = 'in der Nähe ' + (filter.locationName ? 'von ' + filter.locationName : '');
      } else {
        distanceDescription = 'ca. ' + Math.round(distanceKm / 10) * 10 + ' km Luftlinie ' + (filter.locationName ? 'von ' + filter.locationName : '') + ' entfernt';
      }
      vendorsWithDistance.push({
        ...newVendors[i],
        distance,
        distanceDescription
      });
    }
    let newFilteredVendors = vendorsWithDistance.filter(vendor => {
      return vendor.distance < max_distance;
    });
    while (newFilteredVendors.length == 0) {
      max_distance += 0.3;
      newFilteredVendors = vendorsWithDistance.filter(vendor => {
        return vendor.distance < max_distance;
      });
    }
    // order vendors by distance
    newFilteredVendors.sort((a, b) => {
      return a.distance - b.distance;
    });

    setFilteredVendors(newFilteredVendors);
    Object.keys(markers).forEach((vendorId) => {
      // if vendor is in filtered vendors, highlight marker
      if (newFilteredVendors.find(vendor => vendor.id == vendorId)) {
        markers[vendorId].addClassName('highlighted');
      } else {
        markers[vendorId].removeClassName('highlighted');
      }
    });
    if (!fitBounds) return;
    map.current.flyTo({
      center: [filter.lng, filter.lat],
    });
    const filteredVendorsBoundingBox = new mapboxgl.LngLatBounds();
    newFilteredVendors.forEach(vendor => {
      filteredVendorsBoundingBox.extend([vendor.lng, vendor.lat]);
    });
    map.current.fitBounds(filteredVendorsBoundingBox, { padding: 100 });
  }


  return (
    <>
      <AddressSearch
        onAddressSelect={(address => {
          setFilter({
            locationName: address.name,
            lat: parseFloat(address.lat),
            lng: parseFloat(address.lon)
          });
          setLat(parseFloat(address.lat));
          setLng(parseFloat(address.lon));
          if (searchCenterMarker) {
            searchCenterMarker.remove();
          }
          const centerMarkerElement = document.createElement('div');
          centerMarkerElement.className = 'search-center-marker';

          const newCenterMarker = new mapboxgl.Marker({ element: centerMarkerElement, anchor: 'center' })
            .setLngLat([address.lon, address.lat])
            .addTo(map.current);
          setSearchCenterMarker(newCenterMarker);
          setZoom(16);
        })}
        postalCode={preselectedVendorId === null ? postalCode : null} // only prefill postal code if no vendor is preselected, otherwise the postal code of the preselected vendor will be used which can lead to confusion
      />
      <div ref={mapContainer} className="vendor-map-container" />
      {isLoading ? <div className='loading'>Lade Händler...</div> : null}
    </>
  );
}
